/**
 * Live "does this meeting have a summary the user can read?" selector.
 *
 * The dashboard's CTA label depends on this. Computing it once while mapping the
 * quickfetch payload (eventMapper's `hasReadableMeetingNotes`, which produces
 * `CalendarEvent.hasSummary`) makes it a snapshot: a summary generated while the
 * dashboard is open never flips the label, because the WebSocket updates the CRM
 * store while the mapped event object stays frozen at its map-time value.
 *
 * Reading the store instead makes the label track the record. `applyEvent.ts`
 * applies `ahoy_association_added` straight to the source entity in the store,
 * so the edge lands within a frame; the notes body follows on the debounced
 * related-query refetch, which re-upserts through `upsertListResponseToStore`.
 *
 * ## Two traps this deliberately avoids
 *
 * 1. **Summary entries.** Objects hydrated from a list response's
 *    `associated_objects` are stored with `isComplete: false`. `subscribeToObject`
 *    (and therefore `useCrmObject`) returns `undefined` for those, so a selector
 *    built on it would report `false` for every meeting, forever — a silently
 *    always-wrong CTA. `useStoreObjectsMap` reads `stored.data` without an
 *    `isComplete` check, which is why it is the correct primitive here. It also
 *    resolves post-merge entity aliases, which matters because a
 *    `target_entity_id` can be a pre-merge id.
 *
 * 2. **Existence-leak.** This requires the notes object to RESOLVE in the store,
 *    not merely to be referenced by an association edge. The backend
 *    permission-checks only an association's SOURCE, so a meeting the user can
 *    read will happily carry an edge to notes they cannot. Trusting the edge
 *    alone would leak the existence of a private summary and would disagree with
 *    the query endpoint, which filters targets server-side. Presence in the store
 *    means a permission-filtered response returned the object. Do not "simplify"
 *    this to an edge-presence check.
 */

import { useMemo } from 'react';

import { useStoreObjectsMap } from '@/lib/crm/store';

/**
 * Meeting -> meeting-notes edge, queried from the MEETING side.
 *
 * The notes-side edge (`ahoy_meeting_notes_to_meeting`) returns nothing when
 * queried from the meeting. eventMapper.ts holds the twin of this constant for
 * the payload-derived fallback path.
 */
const MEETING_NOTES_ASSOCIATION_TYPE = 'ahoy_meeting_to_meeting_notes';

/** Stable empty array so the no-associations case keeps a constant reference. */
const NO_IDS: string[] = [];

/**
 * @param meetingEntityId - The meeting whose summary presence is being checked.
 * @param fallback - Value to use when the meeting is not in the store, normally
 *   the map-time `CalendarEvent.hasSummary`. The dashboard upserts list results
 *   into the store before rendering, so this is a safety net rather than the
 *   usual path — but it guarantees this hook can only ever ADD liveness, never
 *   regress a correct label to a blank one if store hydration is missing.
 */
export function useHasReadableMeetingNotes(
  meetingEntityId: string,
  fallback: boolean
): boolean {
  const meetingIds = useMemo(() => [meetingEntityId], [meetingEntityId]);
  const meetingById = useStoreObjectsMap(meetingIds);
  const meeting = meetingById.get(meetingEntityId);

  const associations = meeting?.associations;
  const noteTargetIds = useMemo(() => {
    if (!associations) {
      return NO_IDS;
    }
    const ids = associations
      .filter(a => a.association_type_name === MEETING_NOTES_ASSOCIATION_TYPE)
      .map(a => a.target_entity_id)
      .filter(Boolean);
    return ids.length > 0 ? ids : NO_IDS;
  }, [associations]);

  const notesById = useStoreObjectsMap(noteTargetIds);

  // All hooks have run; safe to branch.
  if (!meeting) {
    return fallback;
  }
  return noteTargetIds.some(id => notesById.has(id));
}
